"use client";

import { useAuth } from "@/contexts/AuthContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { usersAdapter } from "@/lib/adapters/firestore";
import { NotificationPreferences } from "@/types";
import { Switch } from "@heroui/react";
import { addToast } from "@heroui/toast";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

export function NotificationPreferencesForm() {
  const { user, refreshUser } = useAuth();
  const { isEnabled: pushEnabled } = usePushNotifications();
  const t = useTranslations();
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    globalTournamentNotifications: true,
    organizerNotifications: true,
    otherTeamMatchScheduled: true,
    otherTeamChallenges: true,
    myTeamChallenges: true,
    applicationNotifications: true,
    tournamentChatNotifications: true,
    matchChatNotifications: true,
  });
  const [updating, setUpdating] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (user?.notificationPreferences) {
      setPreferences({
        globalTournamentNotifications:
          user.notificationPreferences.globalTournamentNotifications ?? true,
        organizerNotifications:
          user.notificationPreferences.organizerNotifications ?? true,
        otherTeamMatchScheduled:
          user.notificationPreferences.otherTeamMatchScheduled ?? true,
        otherTeamChallenges:
          user.notificationPreferences.otherTeamChallenges ?? true,
        myTeamChallenges: user.notificationPreferences.myTeamChallenges ?? true,
        applicationNotifications:
          user.notificationPreferences.applicationNotifications ?? true,
        tournamentChatNotifications:
          user.notificationPreferences.tournamentChatNotifications ?? true,
        matchChatNotifications:
          user.notificationPreferences.matchChatNotifications ?? true,
      });
    }
  }, [user]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (!user) {
    return null;
  }

  const savePreferences = async (newPreferences: NotificationPreferences) => {
    try {
      setUpdating(true);

      // Merge with existing preferences so we never clobber keys this form
      // doesn't manage (e.g. email opt-outs). Firestore replaces the whole
      // `notificationPreferences` map on update.
      await usersAdapter.update(user.id, {
        notificationPreferences: {
          ...user.notificationPreferences,
          ...newPreferences,
        },
      });

      await refreshUser();

      addToast({
        title: t("settings.notificationPreferences.success"),
        color: "success",
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating notification preferences:", error);
      // Revert on error
      setPreferences(user.notificationPreferences || preferences);
      addToast({
        title: t("settings.notificationPreferences.error"),
        color: "danger",
        severity: "danger",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleToggle = (key: keyof NotificationPreferences, value: boolean) => {
    // Update UI immediately for responsiveness
    const newPreferences = { ...preferences, [key]: value };
    setPreferences(newPreferences);

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save for 800ms
    saveTimeoutRef.current = setTimeout(() => {
      savePreferences(newPreferences);
    }, 800);
  };

  // Don't show preferences if push notifications are not enabled
  if (!pushEnabled) {
    return null;
  }

  return (
    <div className="space-y-4 pt-4 border-t border-divider">
      <div>
        <h4 className="text-sm font-medium text-foreground mb-1">
          {t("settings.notificationPreferences.title")}
        </h4>
        <p className="text-xs text-default-500">
          {t("settings.notificationPreferences.description")}
        </p>
      </div>

      <div className="space-y-3">
        {/* Global Tournament Notifications */}
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground">
              {t(
                "settings.notificationPreferences.globalTournamentNotifications.title",
              )}
            </h4>
            <p className="text-xs text-default-400 mt-0.5">
              {t(
                "settings.notificationPreferences.globalTournamentNotifications.description",
              )}
            </p>
          </div>
          <Switch
            size="sm"
            isSelected={preferences.globalTournamentNotifications}
            onValueChange={(value) =>
              handleToggle("globalTournamentNotifications", value)
            }
            isDisabled={updating}
          />
        </div>
        {/* Organizer Notifications */}
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground">
              {t(
                "settings.notificationPreferences.organizerNotifications.title",
              )}
            </h4>
            <p className="text-xs text-default-400 mt-0.5">
              {t(
                "settings.notificationPreferences.organizerNotifications.description",
              )}
            </p>
          </div>
          <Switch
            size="sm"
            isSelected={preferences.organizerNotifications}
            onValueChange={(value) =>
              handleToggle("organizerNotifications", value)
            }
            isDisabled={updating}
          />
        </div>
        {/* Other Team Match Scheduled */}
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground">
              {t(
                "settings.notificationPreferences.otherTeamMatchScheduled.title",
              )}
            </h4>
            <p className="text-xs text-default-400 mt-0.5">
              {t(
                "settings.notificationPreferences.otherTeamMatchScheduled.description",
              )}
            </p>
          </div>
          <Switch
            size="sm"
            isSelected={preferences.otherTeamMatchScheduled}
            onValueChange={(value) =>
              handleToggle("otherTeamMatchScheduled", value)
            }
            isDisabled={updating}
          />
        </div>
        {/* Other Team Challenges */}
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground">
              {t("settings.notificationPreferences.otherTeamChallenges.title")}
            </h4>
            <p className="text-xs text-default-400 mt-0.5">
              {t(
                "settings.notificationPreferences.otherTeamChallenges.description",
              )}
            </p>
          </div>
          <Switch
            size="sm"
            isSelected={preferences.otherTeamChallenges}
            onValueChange={(value) =>
              handleToggle("otherTeamChallenges", value)
            }
            isDisabled={updating}
          />
        </div>
        {/* My Team Challenges */}
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground">
              {t("settings.notificationPreferences.myTeamChallenges.title")}
            </h4>
            <p className="text-xs text-default-400 mt-0.5">
              {t(
                "settings.notificationPreferences.myTeamChallenges.description",
              )}
            </p>
          </div>
          <Switch
            size="sm"
            isSelected={preferences.myTeamChallenges}
            onValueChange={(value) => handleToggle("myTeamChallenges", value)}
            isDisabled={updating}
          />
        </div>
        {/* Application Notifications */}
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground">
              {t(
                "settings.notificationPreferences.applicationNotifications.title",
              )}
            </h4>
            <p className="text-xs text-default-400 mt-0.5">
              {t(
                "settings.notificationPreferences.applicationNotifications.description",
              )}
            </p>
          </div>
          <Switch
            size="sm"
            isSelected={preferences.applicationNotifications}
            onValueChange={(value) =>
              handleToggle("applicationNotifications", value)
            }
            isDisabled={updating}
          />
        </div>
        {/* Tournament Chat Notifications */}
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground">
              {t(
                "settings.notificationPreferences.tournamentChatNotifications.title",
              )}
            </h4>
            <p className="text-xs text-default-400 mt-0.5">
              {t(
                "settings.notificationPreferences.tournamentChatNotifications.description",
              )}
            </p>
          </div>
          <Switch
            size="sm"
            isSelected={preferences.tournamentChatNotifications}
            onValueChange={(value) =>
              handleToggle("tournamentChatNotifications", value)
            }
            isDisabled={updating}
          />
        </div>
        {/* Match Chat Notifications */}
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground">
              {t(
                "settings.notificationPreferences.matchChatNotifications.title",
              )}
            </h4>
            <p className="text-xs text-default-400 mt-0.5">
              {t(
                "settings.notificationPreferences.matchChatNotifications.description",
              )}
            </p>
          </div>
          <Switch
            size="sm"
            isSelected={preferences.matchChatNotifications}
            onValueChange={(value) =>
              handleToggle("matchChatNotifications", value)
            }
            isDisabled={updating}
          />
        </div>
      </div>
    </div>
  );
}
